import React, { useState } from "react";
import { EVENT_EVALUATION_TIMEFRAMES, INSIGHTS_EVENT_TYPES } from "../config/insightsEventTypes";
import { isEligibleReferenceVersion } from "./referenceAvailability";

const STATES = [
  ["enabled", "Ready and enabled"], ["disabled", "Ready and disabled"],
  ["progress", "In progress"], ["missing", "Not created"],
];
const slots = ["S1", "S2", "S3", "F1", "F2", "F3"];
const imageCount = (version) => slots.filter((slot) => version?.slots?.[slot]?.imageId).length;

function describePack(pack) {
  const versions = [...(pack?.versions || [])].sort((a, b) => b.version - a.version);
  const available = versions.filter((version) => !pack.retiredVersions.includes(version.version));
  const active = available.find((version) => version.version === pack.activeVersion && isEligibleReferenceVersion(version));
  const ready = available.find(isEligibleReferenceVersion);
  const state = !pack ? "missing" : pack.enabled && active ? "enabled" : ready ? "disabled" : "progress";
  return { state, latest: versions[0], ready: state === "enabled" ? active : ready };
}

export default function ReferencePackOverview({ packs, selectedKey, disabled, onOpen }) {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const rows = INSIGHTS_EVENT_TYPES.flatMap(({ value: eventType, label }) =>
    ["LONG", "SHORT"].flatMap((direction) => EVENT_EVALUATION_TIMEFRAMES.map((timeframe) => {
      const key = { eventType, direction, timeframe };
      const pack = packs.find((entry) => entry.eventType === eventType && entry.direction === direction && entry.timeframe === timeframe);
      return { ...key, label, ...describePack(pack) };
    })));
  const query = search.trim().toLowerCase();
  const visible = rows.filter((row) => (filter === "all" || row.state === filter)
    && `${row.label} ${row.eventType} ${row.direction} ${row.timeframe}`.toLowerCase().includes(query));
  return <section className="adminReferenceOverview" aria-label="Reference pack overview">
    <h3>Reference pack overview</h3>
    <p>Saved packs across all events, directions and timeframes. Ready means all six verified setup captures are saved in a version that has not been retired. Unverified images must be recaptured for automatic comparison.</p>
    <div className="adminReferenceOverviewFilters" role="group" aria-label="Filter packs by status">
      <button type="button" className="adminSecondaryButton" aria-pressed={filter === "all"} onClick={() => setFilter("all")}>All ({rows.length})</button>
      {STATES.map(([value, label]) => <button type="button" key={value} className="adminSecondaryButton"
        aria-pressed={filter === value} onClick={() => setFilter(value)}>{label} ({rows.filter((row) => row.state === value).length})</button>)}
    </div>
    <label className="adminFormField"><span>Search packs</span><input type="search" value={search}
      placeholder="Event, direction or timeframe" onChange={(event) => setSearch(event.target.value)} /></label>
    <p className="adminFieldHint">Opening a pack shows its latest saved version. An enabled pack may have a newer unfinished version; the active version stays enabled. Unsaved edits are not included in this listing.</p>
    <div className="adminTableViewport adminReferenceOverviewTable" tabIndex={0} role="region" aria-label="Reference packs">
      <table className="adminAuditTable">
        <thead><tr><th scope="col">Event</th><th scope="col">Direction</th><th scope="col">Timeframe</th><th scope="col">Status</th><th scope="col">Latest saved</th><th scope="col">Ready version</th><th scope="col">Action</th></tr></thead>
        <tbody>{visible.map((row) => {
          const current = row.eventType === selectedKey.eventType && row.direction === selectedKey.direction && row.timeframe === selectedKey.timeframe;
          return <tr key={`${row.eventType}-${row.direction}-${row.timeframe}`} className={current ? "adminReferenceOverviewSelected" : undefined}>
            <th scope="row">{row.label}</th><td>{row.direction}</td><td>{row.timeframe}</td>
            <td><span className={`adminStatusBadge adminReferenceState-${row.state}`}>{STATES.find(([value]) => value === row.state)[1]}</span></td>
            <td>{row.latest ? `v${row.latest.version} · ${imageCount(row.latest)}/6 images` : "—"}</td>
            <td>{row.ready ? `v${row.ready.version}${row.state === "enabled" ? " · active" : ""}` : "—"}</td>
            <td><button type="button" className="adminSecondaryButton" disabled={disabled}
              aria-label={`Open ${row.label} ${row.direction} ${row.timeframe} pack`}
              onClick={() => onOpen({ eventType: row.eventType, direction: row.direction, timeframe: row.timeframe })}>{current ? "Selected" : row.state === "missing" ? "Create" : "Open"}</button></td>
          </tr>;
        })}</tbody>
      </table>
      {!visible.length && <p role="status">No packs match these filters.</p>}
    </div>
  </section>;
}
